import { Button, Dialog, Flex, Tabs, Text } from '@radix-ui/themes';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Icon from '~/components/Icon';
import DragAndDropUploadFile from '~/components/Input/Elements/DragAndDropUploadFile';
import RadixGhostButton from '~/components/Input/Elements/RadixGhostButton';
import Loader from '~/components/Loader';
import LoadingOverlay from '~/components/LoadingOverlay';
import SiteContainer from '~/components/SiteContainer';
import { TechnologyBadge } from '~/components/TechnologyBadge';
import useIpxProviders from '~/hooks/use-ipx-providers';
import useNetworkService from '~/hooks/use-network-service';
import Model from '../../../containers/Model';
import ErrorCallout from '../ErrorCallout';
import Menu from '../Menu';

const ORDER = [
  {
    key: 'mccmnc',
    label: 'MCC/MNC',
    valueRenderFunc: ({ mcc, mnc }) => `${mcc} / ${mnc}`,
  },
  {
    key: 'countryCode',
    label: 'Country/Code',
    valueRenderFunc: ({ country, countryCode }) => `${country} / ${countryCode}`,
  },
  { key: 'name', label: 'Network name' },
  {
    key: 'permanentRoamingRestriction',
    label: 'Permanent Roaming Restriction',
    // since the value is boolean we have to add the line below in order to be able to show the actual value of permanentRoamingRestriction
    valueRenderFunc: ({ permanentRoamingRestriction }) => `${permanentRoamingRestriction}`,
  },
  { key: 'ipxProvider', label: 'Provider' },
  {
    key: 'technologies',
    label: 'Technologies',
    valueRenderFunc: ({ '2G': provider2g, '3G': provider3g, '4G': provider4g, lteM, nbIoT }) => {
      return (
        <Flex
          height='100%'
          align='center'
          gap='2'
        >
          {provider2g && TechnologyBadge({ label: '2G' })}
          {provider3g && TechnologyBadge({ label: '3G' })}
          {provider4g && TechnologyBadge({ label: '4G' })}
          {lteM && TechnologyBadge({ label: 'LTE-M' })}
          {nbIoT && TechnologyBadge({ label: 'NB-IOT' })}
        </Flex>
      );
    },
  },
];

function DialogOpenButton({ setOpen }) {
  return (
    <Button
      variant='outline'
      color='gray'
      radius='full'
      onClick={() => setOpen(true)}
    >
      <Flex gap='2'>
        <Icon
          className='icon'
          height='20'
          width='20'
          name='upload-01'
          inline
        />
        <Text>Upload new network list</Text>
      </Flex>
    </Button>
  );
}

DialogOpenButton.propTypes = {
  setOpen: PropTypes.func.isRequired,
};

function UploadDialog({ networkUploadManager, open, setOpen }) {
  function UploadNetwork() {
    networkUploadManager.uploadFile();
    setOpen(false);
  }
  return (
    <Dialog.Root
      open={open}
      onOpenChange={setOpen}
    >
      <Dialog.Content maxWidth='90%'>
        <Flex
          direction='column'
          align='center'
          justify='center'
          gap='4'
        >
          <Text
            size='5'
            weight='bold'
          >
            Upload new network list
          </Text>
          <DragAndDropUploadFile
            allowedMimeTypes={['text/csv']}
            allowedExtensions={['.csv']}
            onFile={file => networkUploadManager.setFile(file)}
            fileTitle='new network list'
          />
        </Flex>
        <Flex
          justify='end'
          align='center'
          width='100%'
          mt='4'
          gap='2'
        >
          <RadixGhostButton
            onClick={() => setOpen(false)}
            color='gray'
          >
            Cancel
          </RadixGhostButton>
          <Button
            variant='outline'
            highContrast
            color='gray'
            size='2'
            radius='full'
            onClick={UploadNetwork}
          >
            Upload
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

UploadDialog.propTypes = {
  networkUploadManager: PropTypes.object,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  confirmNewPricelist: PropTypes.func,
};

function NetworkServicePage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data: ipxProviders, loading } = useIpxProviders();
  const { networkUploadManager, data, setIpxProvider, ipxProvider, pagination, search, error } = useNetworkService();
  const [activeTab, setActiveTab] = useState();
  const modelNetworks = {
    data,
    pagination,
    search,
  };
  function onSegmentedControlChange(value) {
    setIpxProvider(value);
  }

  function onSetDialogOpen(value) {
    if (!value) networkUploadManager.unsetFile();
    setDialogOpen(!!value);
  }

  useEffect(() => {
    setActiveTab(ipxProvider);
  }, [ipxProvider]);

  if (loading || !ipxProviders) return <LoadingOverlay />;

  return (
    <SiteContainer>
      <Menu />
      {error && <ErrorCallout error={error} />}
      <Flex
        direction='column'
        gap='5'
      >
        <Tabs.Root
          defaultValue={ipxProviders?.[0].name}
          onValueChange={onSegmentedControlChange}
          value={activeTab}
        >
          <Tabs.List>
            {ipxProviders?.map(ipxProvider => {
              return (
                <Tabs.Trigger
                  key={ipxProvider.name}
                  value={ipxProvider.name}
                >
                  {ipxProvider.name}
                </Tabs.Trigger>
              );
            })}
          </Tabs.List>
        </Tabs.Root>
        <UploadDialog
          networkUploadManager={networkUploadManager}
          open={dialogOpen}
          setOpen={onSetDialogOpen}
        />
        {networkUploadManager.loading === true ? (
          <Loader />
        ) : (
          <Model
            noun='Network'
            order={ORDER}
            model={modelNetworks}
            customHeaderAction={() => <DialogOpenButton setOpen={setDialogOpen} />}
          />
        )}
      </Flex>
    </SiteContainer>
  );
}

export default NetworkServicePage;
